import React from 'react';
import {
    Grid,
    IconButton,
} from '@material-ui/core';
import { ArrowUpward, ArrowDownward, Delete as DeleteIcon, Edit } from '@material-ui/icons';
import { AwardingRuleParameterType, getAwardingRuleParameterTypeLabel, getOrderLabel, DataType } from '../../../../Enums';
import { useTranslation } from 'react-i18next';
import InputTitle from '../InputTitle';
import { getHyperfindPriorityLabel } from './HyperfindParameter.service';

import './Step2-SortingRuleSection.scss';

export const Direction = Object.freeze({
    Up: -1,
    Down: 1,
});

/**
 *
 * @param {{
 *  awardingRules: ApiAdmin.IBodyAwardingRules,
 *  handleMove: Function,
 *  handleEdit: Function,
 *  handleDelete: Function,
 *  isDisabled: boolean,
 *  adminAppData: AdminAppData.IAppData,
 * }} props
 * @returns
 */
function SortingRulesSummary(props) {
    const { t } = useTranslation('employmentBillboard', { keyPrefix: 'settings.ruleSet.sortingRuleStep' });
    const { t: tError } = useTranslation('employmentBillboard', { keyPrefix: 'admin.warnings' });
    const { awardingRules, handleMove, handleEdit, handleDelete, isDisabled, adminAppData } = props;
    const { hyperfinds, dataviews, customDateFormat, customDataFields } = adminAppData;

    /**
     *
     * @param {DataType} dataType
     * @param {number} customDateFormatId
     * @returns
     */
    const getCustomDateFormatLabel = (dataType, customDateFormatId) => {
        if (dataType === DataType.Numeric) {
            return 'Numeric';
        }
        const selectedFormat = customDateFormat.find((x) => x.id === customDateFormatId);
        if (selectedFormat) {
            if (selectedFormat.dataType === DataType.Date) {
                return `Date ${selectedFormat.name}`;
            }
            if (selectedFormat.dataType === DataType.Time) {
                return `Time ${selectedFormat.name}`;
            }
        }
        return '';
    }
    /**
     *
     * @param {ApiAdmin.IBodyAwardingRules} x
     * @returns
     */
    const displayAwardingRuleProperties = (x) => {
        switch (x.type) {
            case AwardingRuleParameterType.CustomDataType: {
                return (
                    <>
                        <Grid item xs={3}>
                            <span className="text-lg-medium" style={{ width: '38%' }}>
                                {getAwardingRuleParameterTypeLabel(x.type)}
                            </span>
                        </Grid>
                        <Grid item xs={4}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {customDataFields.find(c => c.id === x.customDataFieldInfos.customDataFieldId).name}
                            </span>
                        </Grid>
                        <Grid item xs={3}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getCustomDateFormatLabel(x.customDataFieldInfos.dataType, x.customDataFieldInfos.customDateFormat)}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getOrderLabel(x.order)}
                            </span>
                        </Grid>
                    </>
                )
            }
            case AwardingRuleParameterType.HyperfindBelonging: {
                return (
                    <>
                        <Grid item xs={3}>
                            <span className="text-lg-medium" style={{ width: '51%' }}>
                                {getAwardingRuleParameterTypeLabel(x.type)}
                            </span>
                        </Grid>
                        <Grid item xs={7}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {hyperfinds.find(h => h.value === x.hyperfindBelongingInfos.hyperfindId)?.label}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getHyperfindPriorityLabel(x.order)}
                            </span>
                        </Grid>
                    </>
                )
            }
            case AwardingRuleParameterType.DataviewData: {
                const dataview = dataviews.find((d) => d.id === x.dataviewInfos.dataviewId);
                const dataviewField = dataview?.fields.find(f => f.key === x.dataviewInfos.dataviewKeyAlias);
                return (
                    <>
                        <Grid item xs={3}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getAwardingRuleParameterTypeLabel(x.type)}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%', color: !dataview?.name ? 'red' : 'inherit' }}>
                                {dataview?.name ?? tError('dataviewNotFound')}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%', color: !dataviewField?.label ? 'red' : 'inherit' }}>
                                {dataviewField?.label ?? tError('dataviewNotFound')}
                            </span>
                        </Grid>
                        <Grid item xs={3}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getCustomDateFormatLabel(x.dataviewInfos.dataType, x.dataviewInfos.customDateFormat)}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getOrderLabel(x.order)}
                            </span>
                        </Grid>
                    </>
                )
            }
            case AwardingRuleParameterType.BySubmittedRequestDateTime:
                return (
                    <>
                        <Grid item xs={12}>
                            <span className="text-lg-medium" style={{ width: '80%' }}>
                                {getAwardingRuleParameterTypeLabel(x.type)}
                            </span>
                        </Grid>
                        <Grid item xs={2}>
                            <span className="text-lg-medium" style={{ width: '17%' }}>
                                {getOrderLabel(x.order)}
                            </span>
                        </Grid>
                    </>
                )
            default:
                break;
        }
    }
    return (
        <>
            <InputTitle className="sortingRules-summary-title">{t('sortingOrder')}</InputTitle>
            {awardingRules.map((x, index) => (
                <Grid
                    container
                    justifyContent="space-evenly"
                >
                    <Grid
                        item
                        xs={awardingRules.length > 1 ? 2 : 1}
                        className="rse--selection-field-container"
                    >
                        <InputTitle className="srs--summary-sorting-rule-index">{`${index + 1}.`}</InputTitle>
                        {awardingRules.length > 1 && (<div>
                            <IconButton
                                onClick={() => handleMove(index, Direction.Up)}
                                className="srs--arrow-btn"
                                disabled={isDisabled || index === 0}
                            >
                                <ArrowUpward />
                            </IconButton>
                            <IconButton
                                onClick={() => handleMove(index, Direction.Down)}
                                className="srs--arrow-btn"
                                disabled={isDisabled || awardingRules.length === index + 1}
                            >
                                <ArrowDownward />
                            </IconButton>
                        </div>)}
                    </Grid>

                    <Grid
                        item
                        xs={awardingRules.length > 1 ? 8 : 9}
                        className="srs--awarding-rule-summary"
                    >
                        {displayAwardingRuleProperties(x)}
                    </Grid>

                    <Grid
                        item
                        xs={2}
                        className="srs--summary-edit-delete-btns"
                        justifyContent="flex-end"
                    >
                        <IconButton
                            onClick={() => handleEdit(index)}
                            className="srs--edit-and-delete-btn"
                            disabled={isDisabled}
                        >
                            <Edit />
                        </IconButton>
                        <IconButton
                            onClick={() => handleDelete(index)}
                            className="srs--edit-and-delete-btn"
                            disabled={isDisabled}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            ))}
        </>
    );
}
export default SortingRulesSummary;
